import React from 'react';

import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
	Box,
	Card,
	CardActionArea,
	FormControlLabel,
	Radio,
	RadioGroup,
	Step,
	StepLabel,
	Stepper,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material';

import { OperationExecutionTypeModel, OperationExecutionTypesModel, OperationExposureModel, SchemaSummaryModel } from '../../../api/api';
import JumboSelect from '../../../components/Controls/JumboSelect';
import { OperationExposureChannel, channelsToExposure, exposureToChannels } from '../Utils/operationExposure';

const steps = ['Basic Info', 'Operation Type', 'Template'];

export type OperationFormData = {
	name: string;
	description: string;
	exposure: OperationExposureModel;
	selectedTypeIds: OperationExecutionTypesModel[];
	creationMode: 'blank' | 'template';
	selectedEntityIds: string[];
};

export const operationWizardStepCount = steps.length;

export const isOperationWizardStepValid = (formData: OperationFormData, activeStep: number) => {
	switch (activeStep) {
		case 0:
			return Boolean(formData.name.trim() && formData.description.trim());
		case 1:
			return formData.selectedTypeIds.length > 0;
		case 2:
			return formData.creationMode === 'blank' || (formData.selectedEntityIds.length > 0 && formData.creationMode === 'template');
		default:
			return false;
	}
};

const operationTypeIconMap: Record<OperationExecutionTypesModel, React.ReactElement> = {
	[OperationExecutionTypesModel.Create]: <AddBoxOutlinedIcon />,
	[OperationExecutionTypesModel.Read]: <VisibilityOutlinedIcon />,
	[OperationExecutionTypesModel.Update]: <EditOutlinedIcon />,
	[OperationExecutionTypesModel.Delete]: <DeleteOutlineIcon />,
	[OperationExecutionTypesModel.Execute]: <PlayCircleOutlineIcon />,
};

interface OperationStepperProps {
	activeStep: number;
	formData: OperationFormData;
	onFormDataChange: (data: OperationFormData) => void;
	executionTypes: OperationExecutionTypeModel[];
	availableTargetEnitities: SchemaSummaryModel[];
}

export default function OperationStepper({ activeStep, formData, onFormDataChange, executionTypes, availableTargetEnitities }: OperationStepperProps) {
	const updateFormData = (data: OperationFormData) => {
		onFormDataChange(data);
	};

	const handleSetSelectedTypeIds = (selected: OperationExecutionTypesModel[]) => {
		updateFormData({
			...formData,
			selectedTypeIds: selected,
		});
	};

	const handleSetSelectedEntityIds = (selected: string[]) => {
		updateFormData({
			...formData,
			selectedEntityIds: selected,
		});
	};

	return (
		<Box sx={{ width: '100%', maxWidth: 600, mx: 'auto', display: 'flex', flex: 1, flexDirection: 'column' }}>
			<Stepper activeStep={activeStep} alternativeLabel>
				{steps.map((label) => (
					<Step key={label}>
						<StepLabel>{label}</StepLabel>
					</Step>
				))}
			</Stepper>

			<Box sx={{ alignItems: 'center', display: 'flex', flex: 1, mt: 4 }}>
				{activeStep === 0 && (
					<Box sx={{ width: '100%' }}>
						<TextField
							fullWidth
							label="Operation Name"
							value={formData.name}
							onChange={(e) => {
								const sanitized = e.target.value
									.replace(/\s+/g, '-') // Replace spaces with dashes
									.replace(/[^a-zA-Z0-9-_]/g, '') // Remove special characters
									.toLowerCase(); // Optional: lowercase
								updateFormData({ ...formData, name: sanitized });
							}}
							sx={{ mb: 2 }}
						/>
						<TextField
							fullWidth
							label="Description"
							multiline
							rows={3}
							value={formData.description}
							onChange={(e) => updateFormData({ ...formData, description: e.target.value })}
							sx={{ mb: 2 }}
						/>
						<Typography id="operation-exposure-label" variant="subtitle2" sx={{ mb: 1 }}>
							Exposure
						</Typography>
						<ToggleButtonGroup
							fullWidth
							size="small"
							aria-labelledby="operation-exposure-label"
							value={exposureToChannels(formData.exposure)}
							onChange={(_, channels: OperationExposureChannel[]) => {
								if (channels.length === 0) {
									return;
								}

								updateFormData({ ...formData, exposure: channelsToExposure(channels) });
							}}
						>
							<ToggleButton value="api">API</ToggleButton>
							<ToggleButton value="mcp">MCP</ToggleButton>
						</ToggleButtonGroup>
					</Box>
				)}

				{activeStep === 1 && (
					<Box
						sx={{
							display: 'grid',
							gap: 1.25,
							gridTemplateColumns: {
								xs: '1fr',
								sm: 'repeat(2, minmax(0, 1fr))',
								md: 'repeat(3, minmax(0, 1fr))',
							},
						}}
					>
						{executionTypes.map((executionType) => {
							const isSelected = formData.selectedTypeIds.includes(executionType.id);

							return (
								<Card
									key={executionType.id}
									variant="outlined"
									sx={{
										borderColor: isSelected ? 'primary.main' : 'divider',
										bgcolor: isSelected ? 'action.selected' : 'background.paper',
									}}
								>
									<CardActionArea
										onClick={() => handleSetSelectedTypeIds([executionType.id])}
										sx={{
											alignItems: 'flex-start',
											display: 'flex',
											gap: 1.25,
											minHeight: 96,
											p: 1.5,
										}}
									>
										<Box
											sx={{
												alignItems: 'center',
												color: isSelected ? 'primary.main' : 'text.secondary',
												display: 'flex',
												flex: '0 0 auto',
												mt: 0.25,
											}}
										>
											{operationTypeIconMap[executionType.id]}
										</Box>
										<Box sx={{ minWidth: 0 }}>
											<Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
												{executionType.name}
											</Typography>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
												{executionType.description}
											</Typography>
										</Box>
									</CardActionArea>
								</Card>
							);
						})}
					</Box>
				)}

				{activeStep === 2 && (
					<Box sx={{ width: '100%' }}>
						<Typography id="creation-mode-label" variant="subtitle2" sx={{ mb: 1 }}>
							Creation Mode
						</Typography>
						<RadioGroup
							aria-labelledby="creation-mode-label"
							value={formData.creationMode}
							onChange={(e) =>
								updateFormData({
									...formData,
									creationMode: e.target.value as 'blank' | 'template',
									selectedEntityIds: [],
								})
							}
						>
							<FormControlLabel value="blank" control={<Radio />} label="Create from empty" />
							<FormControlLabel value="template" control={<Radio />} label="Create with template" />
						</RadioGroup>

						{formData.creationMode === 'template' && (
							<>
								<Typography id="entity-label" variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
									Select a target entity
								</Typography>
								<JumboSelect
									items={availableTargetEnitities.map((et) => ({
										id: String(et.id.toString()),
										name: et.name,
										description: et.description,
									}))}
									multiSelect={false}
									selectedItemIds={formData.selectedEntityIds}
									onSelectionChange={handleSetSelectedEntityIds}
								/>
							</>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
}
